import { FC, useState, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import StepLinkList from '@/components/UI/StepLinkList';
import { AnimalSelectionTable } from './AnimalSelectionTable';
import { RegisteredAnimalTarget } from '@/model/SamplePlan.model.ts';
import { RouteService } from '@/support/RouteService.ts';
import { AnimalForSampling } from './AnimalSelectionTable.utils.ts';
import { StudyApiId } from '@/model/Study.model.ts';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook.ts';
import Loading from '@/components/Loading/Loading.tsx';
import { AnimalApiId } from '@/model/Animal.model.ts';
import Header from '@/components/UI/Header';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model.ts';

interface ExecuteSamplesFromPlanProps {
  testId?: string;
}

const ExecuteSamplesSelectAnimalsStep: FC<ExecuteSamplesFromPlanProps> = ({ testId = 'execute-samples-from-plan' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: studyId } = useParams();

  // Get sample definition IDs from location state
  const sampleDefinitionIds = location.state?.sampleDefinitionIds ?? [];

  // Get study API ID from the study data
  const { response: studyResponse, loading: studyLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.show.p',
    path: { id: studyId },
    options: { invokeOnInit: true },
  });

  const studyApiId = useMemo(() => {
    if (studyResponse?.type === 'success') {
      return (studyResponse.body as { data: { api_id: StudyApiId } }).data.api_id;
    }
    return null;
  }, [studyResponse]);

  // Generate back link to sample plan table
  const backLink = useMemo(() => {
    return RouteService.web({
      route: 'studies.samples',
      path: { id: studyId },
    }).route;
  }, [studyId]);

  const [selectedAnimals, setSelectedAnimals] = useState<Set<RegisteredAnimalTarget['api_id']>>(new Set());
  const [animalsData, setAnimalsData] = useState<Array<AnimalForSampling>>([]);

  // Step form configuration
  const steps = [
    {
      title: 'Select animals',
      complete: false, // Currently on this step
    },
    {
      title: 'Collect samples',
      complete: false, // Future step
    },
  ];

  const currentStep = 1; // Currently on step 1 (Select animals)

  const handleSelectionChange = (selectedAnimals: Set<RegisteredAnimalTarget['api_id']>) => {
    setSelectedAnimals(selectedAnimals);
  };

  const handleContinue = () => {
    if (selectedAnimals.size === 0 || !studyApiId) {
      // Could add error toast here if needed
      return;
    }

    // Create map of animalApiId => animalDefinitionApiId for planned animals
    const animalIdDefinitionMap = new Map<AnimalApiId, AnimalDefinitionApiId>();
    animalsData.forEach((animal) => {
      if (animal.animal_definition_api_id) {
        animalIdDefinitionMap.set(animal.api_id, animal.animal_definition_api_id);
      }
    });

    // Navigate to collect step with data in state
    const collectRoute = RouteService.web({
      route: 'studies.samples.execute.collect',
      path: { id: studyId },
    }).route;

    navigate(collectRoute, {
      state: {
        sampleDefinitionIds,
        selectedAnimals: Array.from(selectedAnimals),
        studyApiId,
        animalIdDefinitionMap: animalIdDefinitionMap,
      },
    });
  };

  const handleCancel = () => {
    navigate(backLink, { state: { active_tab: 'sample_plan_table' } });
  };

  // Show loading while we're fetching the study data
  if (studyLoading || !studyApiId) {
    return <Loading />;
  }

  // If no sample definitions were provided in state, redirect back to sampling plan
  if (sampleDefinitionIds.length === 0) {
    return (
      <div data-testid={testId}>
        <div className="ph4 pv3">
          <p>No planned sample groups selected. Please return to the sampling plan to select samples to collect.</p>
          <button type="button" className="btn plain" onClick={handleCancel}>
            Return to Sampling Plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <div className="ph4 pb3">
        <div className="w-100 pb4">
          <Header mainHeaderText="Collect Samples" />
          <StepLinkList style={{ width: '275px' }} steps={steps} activeStep={currentStep} />
        </div>

        <div className="mb4">
          <AnimalSelectionTable
            studyApiId={studyApiId}
            onSelectionChange={handleSelectionChange}
            sampleDefinitionIds={sampleDefinitionIds}
            onAnimalsDataChange={setAnimalsData}
            collectionType="collected"
          />
        </div>

        <div className="flex">
          <button
            type="button"
            className="btn-primary mr3"
            onClick={handleContinue}
            disabled={selectedAnimals.size === 0}
            data-testid={`${testId}__continue-button`}
          >
            Continue
          </button>
          <button type="button" className="btn plain" onClick={handleCancel} data-testid={`${testId}__cancel-button`}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecuteSamplesSelectAnimalsStep;
